<!--
  Two panes, one calculation.

  The old layout listed the catalogue twice — once as a price list at the top,
  again as a "what it buys" summary at the bottom that was an empty dashed box
  until the buyer typed. Same labels, same icons, twice over (four rows then;
  six since port calls), and the second copy was dead space at exactly the
  moment the amount is being decided.

  Here the price list IS the summary. The top card sets an amount; the
  catalogue's last column answers it, row by row, as it changes. Nothing is
  listed twice and nothing is held empty waiting for input.
-->
<div class="vcp">
  <div class="vcp__head">
    <h4 class="vcp__title">Buy Credits</h4>
  </div>

  <!-- The scroller is the full width of the dialog; the 780px reading cap lives
       on the wrapper inside it. Capping the scroller itself parked the bar
       against the cards, floating ~60px in from the dialog edge with nothing
       but background either side of it. -->
  <div class="vcp__body" id="scrollcustom">
    <div class="vcp__body-inner">
      <p class="vcp__lede">
        Credits are spent on the actions below, any time while your time of interest (TOI) is open.
      </p>

      <form class="vcp__grid" [formGroup]="creditFormGroup" (ngSubmit)="buyCredits()">
        <!-- ══ the amount ══════════════════════════════════════════════════ -->
        <section class="vcp__card vcp__amount">
          <h5 class="vcp__card-title">How many?</h5>

          <!-- The presets lead: they are how nearly every purchase is made, and
               the amounts they carry are also the range statement — the first
               and last chip ARE the bounds, so a separate min/max scale under a
               slider was saying the same thing twice. Typing is the fine
               adjustment underneath, not the way in. -->
          <div class="vcp__presets">
            <button
              type="button"
              class="vcp__preset"
              *ngFor="let chip of creditChips"
              [class.is-on]="creditsEntered === chip"
              (click)="setCredits(chip)"
            >
              <span class="vcp__preset-qty">{{ formatCredits(chip) }}</span>
              <span class="vcp__preset-cost" *ngIf="showCreditPricing">
                {{ costLabel(chip * creditUnitPrice) }}
              </span>
            </button>
          </div>

          <!-- One row, one equation: what you enter on the left, what it costs
               on the right. The rate chip sits between them, so amount × rate =
               total reads straight across. -->
          <div class="vcp__row">
            <div class="vcp__entry">
              <!-- A bespoke field rather than mat-form-field outline. The unit
                   belongs inside the box: as a sibling <span> it sat on the
                   outline field's baseline, not the input text's, and needed a
                   hardcoded 20px bottom margin to fake alignment. -->
              <div class="vcp__field-row">
                <div class="vcp__field" [class.is-bad]="!!creditMessage">
                  <input
                    class="vcp__field-input"
                    type="number"
                    inputmode="numeric"
                    formControlName="credits"
                    aria-label="Credits to buy"
                    placeholder="{{ creditChips[1] || minCredits }}"
                    [attr.min]="minCredits"
                    [attr.max]="maxCredits"
                  />
                  <span class="vcp__field-unit">credits</span>
                </div>

                <!-- The bare "× US$ 0.05" is read against the field's own
                     "credits" suffix, an inch to its left. The full sentence is
                     on the label, for anyone reaching this without the layout. -->
                <span
                  class="vcp__rate"
                  *ngIf="showCreditPricing"
                  [attr.aria-label]="costLabel(creditUnitPrice) + ' per credit'"
                >
                  &times; {{ costLabel(creditUnitPrice) }}
                </span>
              </div>

              <!-- Directly under the field it judges, the way every other form
                   in this dialog family reports itself. One line either way —
                   the bounds when there is nothing wrong, the reason when there
                   is — so nothing below it moves on the first keystroke.

                   Announced only when it is an error. Live on the hint too would
                   read the unchanged bounds aloud on every keystroke, over the
                   Total block that is the thing actually changing. -->
              <p
                class="vcp__note"
                [class.is-bad]="!!creditMessage"
                [attr.aria-live]="creditMessage ? 'polite' : null"
              >
                <mat-icon class="material-icons-outlined" *ngIf="creditMessage">
                  error_outline
                </mat-icon>
                <span>{{ creditMessage || rangeHint }}</span>
              </p>
            </div>

            <!-- Always present, valid or not, so the row never changes height. -->
            <div class="vcp__total" [class.is-on]="hasSummary" aria-live="polite">
              <span class="vcp__total-key">Total</span>
              <span class="vcp__total-val">{{ totalLabel }}</span>
              <span class="vcp__total-sub">{{ totalSubLabel }}</span>
            </div>
          </div>
        </section>

        <!-- ══ what that amount reaches ════════════════════════════════════ -->
        <section class="vcp__card vcp__buys">
          <h5 class="vcp__card-title">
            What
            <b *ngIf="hasSummary">{{ formatCredits(creditsEntered) }} credits</b>
            <ng-container *ngIf="!hasSummary">it</ng-container>
            buys
          </h5>

          <!-- Shared with the sensor preview's credit explainer. Binding
               `credits` is what turns the price list into an answer: each row
               grows a "buys N" column, live, as the amount above changes.
               `0` rather than null while the amount is invalid, so the column
               keeps its width and the em-dash placeholder holds the layout. -->
          <app-voyage-credit-catalogue
            [actions]="creditActions"
            [unitPrice]="creditUnitPrice"
            [showPricing]="showCreditPricing"
            [credits]="hasSummary ? creditsEntered : 0"
          ></app-voyage-credit-catalogue>

          <p class="vcp__mix" *ngIf="creditActions.length > 1">…or any mix of these.</p>
        </section>
      </form>
    </div>
  </div>

  <div class="vcp__foot">
    <span class="vcp__foot-note">Unused credits can be refunded on request.</span>
    <button class="custom-button" type="button" (click)="onNoClick()">Cancel</button>
    <button
      mat-stroked-button
      class="submit"
      type="button"
      [disabled]="creditError"
      (click)="buyCredits()"
    >
      {{ buyLabel }}
    </button>
  </div>
</div>
